import { computed, ref } from "vue";
import { hasSellingPrice, arePricelistPricesEnabled, isSellingPriceLocked, Product, Money, minSellingPrice, connectionService, apiService } from "app-core";
const PRICE_ENTRY_DECIMALS = 3;
function changeKey(productId, pricelistId) {
  return `${productId}:${pricelistId ?? 0}`;
}
function unusualChangeMark(oldPrice, newPrice) {
  if (oldPrice <= 0) return "";
  const ratio = newPrice / oldPrice;
  if (ratio < 2 && ratio > 0.5) return "";
  const percent = Math.round(Math.abs(ratio - 1) * 100);
  return ratio > 1 ? `+${percent}%` : `−${percent}%`;
}
function isUnusualChange(oldPrice, newPrice) {
  return unusualChangeMark(oldPrice, newPrice) !== "";
}
function useProductPriceEditor(source) {
  const products = computed(() => source.products().filter((product) => hasSellingPrice(product)));
  const pricelistId = ref(null);
  const categoryId = ref(null);
  const query = ref("");
  const changesOnly = ref(false);
  const staged = ref({});
  const rejected = ref({});
  const selectedId = ref(null);
  const entry = ref("0");
  const fresh = ref(true);
  const entryError = ref(false);
  const atEnd = ref(false);
  const saving = ref(false);
  let saveInFlight = null;
  const pricelists = computed(() => arePricelistPricesEnabled() ? source.pricelists() : []);
  const categories = computed(() => {
    const used = new Set(products.value.map((product) => product.category_id).filter(Boolean));
    return source.categories().filter((category) => used.has(category.id));
  });
  const editableCount = computed(() => products.value.filter((product) => !isSellingPriceLocked(product)).length);
  const changedIds = computed(() => new Set(Object.keys(staged.value).map((key) => key.split(":").map(Number)).filter(([, column]) => column === (pricelistId.value ?? 0)).map(([productId]) => productId)));
  const changedCount = computed(() => changedIds.value.size);
  const rows = computed(() => {
    let inCategory = categoryId.value === null ? products.value : products.value.filter((product) => product.category_id === categoryId.value);
    if (changesOnly.value) inCategory = inCategory.filter((product) => changedIds.value.has(product.id));
    const search = query.value.trim();
    if (!search) return inCategory;
    return Product.search(search, inCategory, {
      limit: inCategory.length,
      disableQuantity: true,
      disableWeightedBarcodes: !source.weightedBarcodes
    }).products;
  });
  const selected = computed(() => products.value.find((product) => product.id === selectedId.value));
  const firstEditable = computed(() => rows.value.find((product) => !isLocked(product)));
  const hasNext = computed(() => {
    const list = rows.value;
    const index = list.findIndex((product) => product.id === selectedId.value);
    return list.slice(index + 1).some((product) => !isLocked(product));
  });
  function isLocked(product) {
    return isSellingPriceLocked(product);
  }
  function currentPrice(product, column = pricelistId.value) {
    if (column === null) return Money.parse(product.selling_price ?? 0).toUnit();
    const price = product.prices.find((row) => row.id === column);
    return price ? Money.parse(price.price).toUnit() : null;
  }
  function referencePrice(product, column = pricelistId.value) {
    return currentPrice(product, column) ?? currentPrice(product, null);
  }
  function stagedPrice(product, column = pricelistId.value) {
    return staged.value[changeKey(product.id, column)];
  }
  function rejectionOf(product, column = pricelistId.value) {
    return rejected.value[changeKey(product.id, column)];
  }
  const changes = computed(() => Object.entries(staged.value).flatMap(([key, price]) => {
    const [productId, column] = key.split(":").map(Number);
    const product = products.value.find((row) => row.id === productId);
    if (!product) return [];
    const pricelist = column || null;
    return [{
      key,
      product,
      pricelistId: pricelist,
      oldPrice: currentPrice(product, pricelist),
      price,
      unusual: isUnusualChange(referencePrice(product, pricelist), price)
    }];
  }));
  const unusualChanges = computed(() => changes.value.filter((change) => change.unusual));
  const rejectedChanges = computed(() => changes.value.filter((change) => rejected.value[change.key]));
  function setStaged(next) {
    staged.value = next;
    if (!changedCount.value) changesOnly.value = false;
  }
  function resetEntry() {
    entry.value = "0";
    fresh.value = true;
    entryError.value = false;
    atEnd.value = false;
  }
  function setEntry(value) {
    entry.value = value;
    entryError.value = false;
    atEnd.value = false;
  }
  function typeKey(key) {
    var _a;
    const current = fresh.value ? "0" : entry.value;
    let next = current;
    if (key === "Backspace") {
      next = current.slice(0, -1).replace(/\.$/, "") || "0";
    } else if (key === "." || key === ",") {
      if (!current.includes(".")) next = `${current}.`;
    } else if (/^[0-9]$/.test(key)) {
      if ((((_a = current.split(".")[1]) == null ? void 0 : _a.length) ?? 0) >= PRICE_ENTRY_DECIMALS) return;
      next = current === "0" ? key : current + key;
    } else {
      return;
    }
    fresh.value = false;
    setEntry(next);
  }
  function commit() {
    const product = selected.value;
    if (!product || fresh.value) return true;
    const price = Money.parse(entry.value || "0").toUnit();
    if (price < minSellingPrice(product)) {
      entryError.value = true;
      return false;
    }
    const key = changeKey(product.id, pricelistId.value);
    const nextStaged = { ...staged.value };
    if (price === currentPrice(product)) delete nextStaged[key];
    else nextStaged[key] = price;
    setStaged(nextStaged);
    if (rejected.value[key]) {
      const nextRejected = { ...rejected.value };
      delete nextRejected[key];
      rejected.value = nextRejected;
    }
    resetEntry();
    return true;
  }
  function select(productId) {
    const product = products.value.find((row) => row.id === productId);
    if (!product || isLocked(product)) return false;
    if (!commit()) return false;
    selectedId.value = productId;
    resetEntry();
    return true;
  }
  function move(step) {
    if (!commit()) return "invalid";
    const list = rows.value;
    const index = list.findIndex((product) => product.id === selectedId.value);
    for (let i = index + step; i >= 0 && i < list.length; i += step) {
      if (!isLocked(list[i])) {
        selectedId.value = list[i].id;
        resetEntry();
        return "moved";
      }
    }
    if (index === -1 && selectFirstEditable()) return "moved";
    if (step === 1) atEnd.value = true;
    return "end";
  }
  function pickFirst() {
    if (!commit()) return "invalid";
    if (!rows.value.length) return "none";
    return selectFirstEditable() ? "selected" : "locked";
  }
  function clearSelection() {
    if (!commit()) return;
    selectedId.value = null;
    resetEntry();
  }
  function setQuery(value) {
    query.value = value;
    if (commit() && !rows.value.some((product) => product.id === selectedId.value)) {
      selectedId.value = null;
      resetEntry();
    }
  }
  function selectFirstEditable() {
    const first = firstEditable.value;
    if (!first) return false;
    selectedId.value = first.id;
    resetEntry();
    return true;
  }
  function setChangesOnly(value) {
    if (!commit()) return false;
    changesOnly.value = value && changedCount.value > 0;
    if (changesOnly.value) categoryId.value = null;
    if (!rows.value.some((product) => product.id === selectedId.value)) selectFirstEditable();
    return true;
  }
  function setCategory(value) {
    if (!commit()) return false;
    categoryId.value = value;
    if (!rows.value.some((product) => product.id === selectedId.value)) selectFirstEditable();
    return true;
  }
  function setPricelist(value) {
    if (!commit()) return false;
    pricelistId.value = value;
    if (!changedCount.value) changesOnly.value = false;
    resetEntry();
    return true;
  }
  function revert() {
    if (!selected.value || fresh.value) return false;
    resetEntry();
    return true;
  }
  function unstage(product) {
    const key = changeKey(product.id, pricelistId.value);
    if (product.id === selectedId.value) resetEntry();
    if (staged.value[key] === void 0) return;
    const nextStaged = { ...staged.value };
    delete nextStaged[key];
    setStaged(nextStaged);
    if (rejected.value[key]) {
      const nextRejected = { ...rejected.value };
      delete nextRejected[key];
      rejected.value = nextRejected;
    }
  }
  function discard() {
    setStaged({});
    rejected.value = {};
    resetEntry();
  }
  function jumpTo(product) {
    if (isLocked(product)) return "locked";
    if (!commit()) return "invalid";
    if (!rows.value.some((row) => row.id === product.id)) {
      query.value = "";
      categoryId.value = null;
      changesOnly.value = false;
    }
    selectedId.value = product.id;
    resetEntry();
    return "selected";
  }
  const hasTypedPrice = computed(() => !!selected.value && !fresh.value);
  async function settled() {
    await (saveInFlight == null ? void 0 : saveInFlight.catch(() => void 0));
  }
  function save() {
    if (saveInFlight) return saveInFlight;
    saveInFlight = runSave().finally(() => {
      saveInFlight = null;
    });
    return saveInFlight;
  }
  async function runSave() {
    var _a;
    if (!commit()) return { status: "invalid" };
    if (!changes.value.length) return { status: "nothing" };
    if (!connectionService.isOnline) return { status: "offline" };
    const sent = changes.value.map((change) => ({ key: change.key, price: change.price }));
    saving.value = true;
    try {
      const response = await apiService.patch(source.saveUrl, {
        prices: changes.value.map((change) => ({
          product_id: change.product.id,
          pricelist_id: change.pricelistId,
          price: change.price
        }))
      });
      source.onSaved(response.data.products);
      const nextStaged = { ...staged.value };
      sent.forEach(({ key, price }) => {
        if (nextStaged[key] === price) delete nextStaged[key];
      });
      setStaged(nextStaged);
      rejected.value = {};
      return { status: "saved", count: sent.length };
    } catch (error) {
      const response = error.response;
      const errors = (response == null ? void 0 : response.status) === 422 && Array.isArray((_a = response.data) == null ? void 0 : _a.errors) ? response.data.errors.filter((item) => item.product_id) : [];
      if (!errors.length) return { status: "error" };
      rejected.value = Object.fromEntries(errors.map((item) => [changeKey(item.product_id, item.pricelist_id ?? null), item.code]));
      return { status: "rejected", count: errors.length };
    } finally {
      saving.value = false;
    }
  }
  return {
    products,
    rows,
    categories,
    pricelists,
    editableCount,
    selected,
    firstEditable,
    hasNext,
    changes,
    unusualChanges,
    rejectedChanges,
    pricelistId,
    categoryId,
    query,
    changesOnly,
    changedCount,
    selectedId,
    entry,
    fresh,
    entryError,
    atEnd,
    saving,
    isLocked,
    currentPrice,
    referencePrice,
    stagedPrice,
    rejectionOf,
    hasTypedPrice,
    setEntry,
    typeKey,
    commit,
    select,
    selectFirstEditable,
    move,
    pickFirst,
    clearSelection,
    setQuery,
    setChangesOnly,
    setCategory,
    setPricelist,
    revert,
    unstage,
    discard,
    jumpTo,
    save,
    settled
  };
}
export {
  PRICE_ENTRY_DECIMALS,
  changeKey,
  isUnusualChange,
  unusualChangeMark,
  useProductPriceEditor
};
//# sourceMappingURL=index54.js.map
